import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Search, FileText, Wrench, Eye, ClipboardCheck } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const stepsData = {
  EN: [
    {
      icon: Search,
      step: "01",
      title: "Assessment",
      description: "We begin with a thorough evaluation of your property and your specific requirements. This includes an on-site inspection, documentation of current condition, identification of immediate needs, and understanding your priorities and concerns.",
      details: ["On-site property inspection", "Condition documentation with photos", "Identification of immediate priorities", "Review of existing contractors/suppliers", "Understanding your requirements"]
    },
    {
      icon: FileText,
      step: "02",
      title: "Planning",
      description: "Based on the assessment, we develop a structured plan tailored to your property. This covers regular oversight schedules, maintenance priorities, contractor recommendations, and communication protocols.",
      details: ["Customized management plan", "Oversight schedule definition", "Maintenance priority list", "Contractor/supplier recommendations", "Communication protocol setup"]
    },
    {
      icon: Wrench,
      step: "03",
      title: "Execution",
      description: "We coordinate and oversee all agreed activities. This includes scheduling contractors, managing access, supervising work quality, and handling day-to-day requirements as they arise.",
      details: ["Contractor coordination", "Access management", "On-site supervision when needed", "Quality verification", "Issue resolution"]
    },
    {
      icon: Eye,
      step: "04",
      title: "Supervision",
      description: "Regular property visits ensure ongoing oversight. We check property condition, verify that scheduled work has been completed correctly, and identify any emerging issues requiring attention.",
      details: ["Regular property visits", "Condition monitoring", "Work completion verification", "Issue identification", "Preventive recommendations"]
    },
    {
      icon: ClipboardCheck,
      step: "05",
      title: "Reporting",
      description: "Transparent communication through regular reports. You receive updates on property status, completed work, upcoming requirements, and any recommendations—keeping you fully informed.",
      details: ["Regular status updates", "Photo documentation", "Work completion reports", "Financial summaries", "Recommendations for next steps"]
    }
  ],
  ES: [
    {
      icon: Search,
      step: "01",
      title: "Evaluación",
      description: "Comenzamos con una evaluación exhaustiva de su propiedad y sus requisitos específicos. Esto incluye una inspección in situ, documentación del estado actual, identificación de necesidades inmediatas y comprensión de sus prioridades y preocupaciones.",
      details: ["Inspección in situ de la propiedad", "Documentación del estado con fotos", "Identificación de prioridades inmediatas", "Revisión de contratistas/proveedores existentes", "Comprensión de sus requisitos"]
    },
    {
      icon: FileText,
      step: "02",
      title: "Planificación",
      description: "Basándonos en la evaluación, desarrollamos un plan estructurado adaptado a su propiedad. Esto cubre horarios de supervisión regulares, prioridades de mantenimiento, recomendaciones de contratistas y protocolos de comunicación.",
      details: ["Plan de gestión personalizado", "Definición del calendario de supervisión", "Lista de prioridades de mantenimiento", "Recomendaciones de contratistas/proveedores", "Configuración del protocolo de comunicación"]
    },
    {
      icon: Wrench,
      step: "03",
      title: "Ejecución",
      description: "Coordinamos y supervisamos todas las actividades acordadas. Esto incluye programar contratistas, gestionar accesos, supervisar la calidad del trabajo y manejar los requisitos diarios según surjan.",
      details: ["Coordinación de contratistas", "Gestión de accesos", "Supervisión in situ cuando sea necesario", "Verificación de calidad", "Resolución de problemas"]
    },
    {
      icon: Eye,
      step: "04",
      title: "Supervisión",
      description: "Las visitas regulares a la propiedad aseguran una supervisión continua. Comprobamos el estado de la propiedad, verificamos que el trabajo programado se haya completado correctamente e identificamos cualquier problema emergente que requiera atención.",
      details: ["Visitas regulares a la propiedad", "Monitorización del estado", "Verificación de trabajos completados", "Identificación de problemas", "Recomendaciones preventivas"]
    },
    {
      icon: ClipboardCheck,
      step: "05",
      title: "Informes",
      description: "Comunicación transparente a través de informes regulares. Recibe actualizaciones sobre el estado de la propiedad, trabajos completados, requisitos próximos y cualquier recomendación—manteniéndole totalmente informado.",
      details: ["Actualizaciones de estado regulares", "Documentación fotográfica", "Informes de trabajos completados", "Resúmenes financieros", "Recomendaciones para próximos pasos"]
    }
  ]
};

const benefitsData = {
  EN: [
    { title: "Consistency", desc: "Standardized processes ensure reliable service delivery regardless of the task or timing." },
    { title: "Transparency", desc: "Clear documentation and regular reporting keep you fully informed at all times." },
    { title: "Accountability", desc: "Defined responsibilities and follow-through protocols ensure nothing falls through the cracks." },
    { title: "Prevention", desc: "Regular supervision identifies potential issues before they become costly problems." },
    { title: "Efficiency", desc: "Coordinated activities and established contractor relationships optimize time and cost." },
    { title: "Peace of Mind", desc: "Knowing your property is in capable hands, with structured oversight in place." }
  ],
  ES: [
    { title: "Consistencia", desc: "Procesos estandarizados aseguran una entrega de servicio fiable independientemente de la tarea o el momento." },
    { title: "Transparencia", desc: "Documentación clara e informes regulares le mantienen totalmente informado en todo momento." },
    { title: "Responsabilidad", desc: "Responsabilidades definidas y protocolos de seguimiento aseguran que nada se pierda." },
    { title: "Prevención", desc: "La supervisión regular identifica problemas potenciales antes de que se conviertan en problemas costosos." },
    { title: "Eficiencia", desc: "Actividades coordinadas y relaciones establecidas con contratistas optimizan tiempo y coste." },
    { title: "Tranquilidad", desc: "Saber que su propiedad está en manos capaces, con supervisión estructurada implementada." }
  ]
};

const ApproachPage = () => {
  const { language, t } = useLanguage();
  const steps = stepsData[language];
  const benefits = benefitsData[language];

  return (
    <div data-testid="approach-page">
      <Helmet>
        <title>Our Approach | Property Management Mallorca | Balearic Home Solutions</title>
        <meta name="description" content="Our structured approach to property management in Mallorca. Assessment, planning, execution, supervision, and transparent reporting for international homeowners." />
        <link rel="canonical" href="https://balearichomesolutions.com/approach" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-500 mb-4">
              {t.approachPage.label}
            </p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-navy tracking-tight mb-6">
              {t.approachPage.title}
            </h1>
            <p className="text-stone-600 text-lg leading-relaxed">
              {t.approachPage.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Process Overview */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Visual Process Flow */}
          <div className="hidden md:flex items-center justify-between mb-20 relative">
            <div className="absolute top-1/2 left-0 right-0 h-[1px] bg-stone-200 -translate-y-1/2" />
            {steps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative z-10 flex flex-col items-center"
              >
                <div className="w-16 h-16 bg-navy rounded-full flex items-center justify-center mb-3">
                  <step.icon size={24} className="text-white" strokeWidth={1.5} />
                </div>
                <span className="text-xs font-bold tracking-widest text-navy">{step.step}</span>
                <span className="text-sm font-medium text-stone-600 mt-1">{step.title}</span>
              </motion.div>
            ))}
          </div>

          {/* Detailed Steps */}
          <div className="space-y-16 md:space-y-24">
            {steps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                data-testid={`approach-step-${step.step}`}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-dense' : ''
                }`}
              >
                {/* Visual */}
                <div className={`${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <div className="bg-stone-50 p-12 flex flex-col items-center justify-center">
                    <div className="w-24 h-24 bg-navy rounded-full flex items-center justify-center mb-6">
                      <step.icon size={40} className="text-white" strokeWidth={1.5} />
                    </div>
                    <span className="text-6xl font-heading text-stone-200">{step.step}</span>
                  </div>
                </div>

                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <h2 className="font-heading text-3xl md:text-4xl text-navy tracking-tight mb-4">
                    {step.title}
                  </h2>
                  <p className="text-stone-600 leading-relaxed mb-6">
                    {step.description}
                  </p>
                  <div className="bg-white border border-stone-100 p-6">
                    <h3 className="text-xs font-bold tracking-widest uppercase text-navy mb-4">
                      {t.approachPage.keyActivities}
                    </h3>
                    <div className="space-y-2">
                      {step.details.map((detail, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <span className="w-1 h-1 bg-navy rounded-full mt-2 flex-shrink-0" />
                          <span className="text-sm text-stone-600">{detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-16">
            <h2 className="font-heading text-3xl md:text-4xl text-navy tracking-tight mb-4">
              {t.approachPage.benefitsTitle}
            </h2>
            <p className="text-stone-600 leading-relaxed">
              {t.approachPage.benefitsDesc}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 border-l-2 border-navy"
              >
                <h3 className="font-heading text-lg text-navy mb-2">
                  {benefit.title}
                </h3>
                <p className="text-stone-600 text-sm leading-relaxed">
                  {benefit.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-navy">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="font-heading text-3xl md:text-4xl text-white tracking-tight mb-6">
              {t.approachPage.ctaTitle}
            </h2>
            <p className="text-white/70 leading-relaxed mb-10">
              {t.approachPage.ctaDesc}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid="approach-cta-btn"
                className="bg-white text-navy px-8 py-4 text-xs font-medium uppercase tracking-widest hover:bg-stone-100 transition-all inline-flex items-center gap-2"
              >
                {t.servicesPage.requestConsultation}
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/services"
                className="border border-white/30 text-white px-8 py-4 text-xs font-medium uppercase tracking-widest hover:bg-white/10 transition-all inline-flex items-center justify-center"
              >
                {t.approachPage.viewServices}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ApproachPage;
